import {Injectable, NgZone, signal} from '@angular/core';
import * as dayjs from 'dayjs';
import {Observable, of, switchMap} from 'rxjs';
import {finalize, map, tap} from 'rxjs/operators';
import {LoaderQueueItem} from '../../types/LoaderQueueItem';
import {MetaTag} from '../../types/MetaTag';
import {SearchPlacesData} from '../../types/SearchPlacesData';
import {SearchResult} from '../../types/SearchResult';
import {SearchResultCareProviders} from '../../types/SearchResultCareProviders';
import {SearchResultsPayload} from '../../types/SearchResultsPayload';
import {SearchState} from '../../types/SearchState';
import {SearchSuggestItem} from '../../types/SearchSuggestItem';
import {SearchSuggestPayload} from '../../types/SearchSuggestPayload';
import {SearchWidgetData} from '../../types/SearchWidgetData';
import {SearchWidgetDataItem} from '../../types/SearchWidgetDataItem';
import {UserLocationState} from '../../types/UserLocationState';
import {DefaultValuesService} from '../default-values.service';
import {LoaderService} from '../loader.service';
import {SearchHelperService} from '../search-helper.service';
import {ToolsService} from '../tools.service';
import {SearchApiService} from './search-api.service';
import {SearchDataManagerService} from './search-data-manager.service';
import {SearchStateManagerService} from './search-state-manager.service';
import {BookingTypeIdEnum} from '../../enums/BookingEnums';
import {WebsiteContent} from '../../types/WebsiteContent';
import {SearchPreprocessQuery} from '../../types/SearchPreprocessQuery';
import {WidgetDataTypeEnum} from '../../enums/WidgetDataTypeEnum';
import {PopularItems} from '../../types/PopularItems';
import {SearchPreprocessResponse} from '../../types/SearchPreprocessResponse';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  public searchState = signal<SearchState>(null);
  public userLocationState = signal<UserLocationState>(null);
  public searchResults = signal<SearchResultCareProviders>(null);
  public searchWidgetData = signal<SearchWidgetData>(null);
  public places = signal<SearchPlacesData>(null);
  public websiteContent = signal<WebsiteContent>(null);
  public adCampaignOwner = signal<number>(null);
  public cmsCategories = signal<number[]>(null);
  public metaTags = signal<MetaTag[]>(null);
  public triggerLocationSearch = signal<boolean>(false);

  public latestStateUsed: SearchState;
  private loadedPlacesPayload: SearchResultsPayload;
  private preloadPlacesTimeout = null;

  constructor(
    private tools: ToolsService,
    private defaults: DefaultValuesService,
    private stateManager: SearchStateManagerService,
    private dataManager: SearchDataManagerService,
    private searchApi: SearchApiService,
    private searchHelper: SearchHelperService,
    private loaderService: LoaderService,
    private ngZone: NgZone,
  ) {
    this.searchState.set(this.tools.clone(this.defaults.getDefaultSearchState()));
    this.userLocationState.set(this.tools.clone(this.defaults.getDefaultUserLocationState()));
  }

  public preprocessSearch(query: SearchPreprocessQuery): Observable<SearchPreprocessResponse> {
    return this.searchApi.preprocessSearch(query);
  }

  public getWidgetData(type: WidgetDataTypeEnum): Observable<SearchWidgetDataItem[]> {
    switch (type) {
      case WidgetDataTypeEnum.CAREGIVER_UNIT_TYPES:
        return this.searchApi.getCaregiverUnitTypes();
      case WidgetDataTypeEnum.CARE_SERVICE_TYPES:
        return this.searchApi.getCareServiceTypes();
      case WidgetDataTypeEnum.SYMPTOMS:
        return this.searchApi.getSymptoms();
    }
  }

  public getPopularItems(): Observable<PopularItems> {
    return this.searchApi.getPopularItems();
  }

  public updateSearchState(state: SearchState): void {
    if (!this.tools.isEqual(this.searchState(), state)) {
      this.searchState.set(state);
    }
    // reload search results if search state has changed
    if (
      !this.tools.isEqual(this.searchState(), this.latestStateUsed) &&
      this.searchState().holdResultsReload !== true
    ) {
      this.latestStateUsed = this.tools.clone(this.searchState());
      this.reloadWebsiteContent();
      this.reloadSearchResults();
      this.lazyPreloadPlaces();
    }
  }

  public updateUserLocationState(state: UserLocationState): void {
    if (!this.tools.isEqual(state, this.userLocationState())) {
      this.userLocationState.set(state);
    }
  }

  public softResetState(): void {
    this.updateSearchState(this.stateManager.getStateAfterSoftReset(this.searchState()));
  }

  public freeSearchSuggest(suggestPayload: SearchSuggestPayload): Observable<SearchSuggestItem[]> {
    return this.searchApi.freeSearchSuggest(suggestPayload);
  }

  public reloadPlaces(searchState?: SearchState): void {
    this.cancelLazyPreloadPlaces();
    this.conditionalReloadPlaces(searchState);
  }

  public getSearchResults(): Observable<SearchResult> {
    const state: SearchState = this.searchState();
    const limitToOnlyOneItem: boolean = this.searchHelper.shouldLimitToOnlyOneItem(state);
    const includeSearchResult: boolean = this.searchHelper.shouldDisplaySearchResults(state);
    const payload: SearchResultsPayload = this.searchHelper.generateSearchPayloadFromSearchState(
      state,
      false,
      this.searchHelper.shouldAskForCampaignOwner(state),
      limitToOnlyOneItem,
    );

    if (this.searchHelper.isPayloadValidForSearch(payload)) {
      payload.includeSearchResult = includeSearchResult;
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: 'Laddar resultat...',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      return this.searchApi.getSearchResults(payload, this.searchState()).pipe(
        finalize(() => this.loaderService.removeFromQueue(queryId)),
        map((searchResult) => {
          if (payload.includeAdCampaignOwner !== true) {
            delete searchResult.adCampaignOwner;
          }
          return searchResult;
        }),
      );
    } else {
      payload.includeSearchResult = false;
      return this.searchApi.getSearchResults(payload, state).pipe(
        map((searchResult) => {
          if (payload.includeAdCampaignOwner !== true) {
            delete searchResult.adCampaignOwner;
          }
          return searchResult;
        }),
      );
    }
  }

  public updateLocalCachedSearchResults(results: SearchResult): void {
    if (this.tools.isNil(results)) {
      return;
    }
    if (results.hasOwnProperty('adCampaignOwner')) {
      this.adCampaignOwner.set(results.adCampaignOwner);
    }
    this.metaTags.set(results.metaTags);
    this.cmsCategories.set(results.cmsCategories);
    if (!this.tools.isEqual(this.searchResults(), results.careProviders)) {
      this.searchResults.set(results.careProviders);
    }
  }

  public updateWidgetDataWithRouterLinks(widgetData: SearchWidgetData): SearchWidgetData {
    return this.dataManager.updateWidgetDataWithRouterLinks(widgetData, this.searchState());
  }

  public resetSearchDataSignals(): void {
    this.cmsCategories.set(null);
    this.metaTags.set(null);
    this.adCampaignOwner.set(null);
    this.searchResults.set(null);
    this.websiteContent.set(null);
  }

  public setupTimeslotsUpdate(): Observable<[boolean, SearchResultCareProviders]> {
    return of(this.searchResults()).pipe(
      switchMap((searchResults) => {
        if (searchResults?.hits && !searchResults.timeslotsUpdated) {
          return this.updateTimeslots(searchResults).pipe(map((r) => [true, r]));
        }
        return of([false, searchResults]);
      }),
      tap(([updated, searchResults]: [boolean, SearchResultCareProviders]) => {
        if (updated && !this.tools.isEqual(this.searchResults(), searchResults)) {
          this.searchResults.set(searchResults);
        }
      }),
    );
  }

  private updateTimeslots(searchResults: SearchResultCareProviders) {
    const formatTime = (ts: Date) => dayjs(ts).format('YYYY-MM-DDTHH:mm:ss.SSSZ');

    const careProviderUnitIds = searchResults.hits.filter((hit) => hit.isCustomer).map((hit) => hit.id);

    if (!Array.isArray(careProviderUnitIds) || !careProviderUnitIds.length) {
      return of({
        ...searchResults,
        timeslotsUpdated: true,
      });
    }

    return this.searchApi.getTimeslots(careProviderUnitIds).pipe(
      // Replace previous timeslots on all care services with the updated ones from the API
      map((updatedTimeslots) => ({
        ...searchResults,
        hits: searchResults.hits.map((cpru) => ({
          ...cpru,
          careServices: cpru.careServices.map((cs) => ({
            ...cs,
            timeSlots: this.getTimeslotsForBookingType(
              updatedTimeslots[cpru.id]?.[cs.id]?.map(formatTime) || [],
              cs.careServiceBookingSettings.bookingType.id,
            ),
          })),
        })),
        timeslotsUpdated: true, // Mark the search results as updated so as not to end up in an infinite loop
      })),
    );
  }

  private getTimeslotsForBookingType(timeSlots: string[], bookingTypeId: number): string[] {
    return bookingTypeId === BookingTypeIdEnum.VARDEN ? timeSlots : [];
  }

  private reloadSearchResults(): void {
    this.searchResults.set(null);

    this.getSearchResults().subscribe(
      (result) => {
        this.updateLocalCachedSearchResults(result);
      },
      (error) => {
        this.resetSearchDataSignals();
      },
    );
  }

  private reloadWebsiteContent(): void {
    this.websiteContent.set(null);
    if (
      this.searchState() &&
      (this.searchState().caregiverUnitTypeUrlPath !== '' ||
        this.searchState().careServiceTypeUrlPath !== '' ||
        this.searchState().symptomUrlPath !== '')
    ) {
      this.searchApi.getWebsiteContent(this.searchState()).subscribe(
        (res) => this.websiteContent.set(res),
        (error) => this.websiteContent.set(null),
      );
    }
  }

  private cancelLazyPreloadPlaces() {
    if (this.preloadPlacesTimeout !== null) {
      clearTimeout(this.preloadPlacesTimeout);
      this.preloadPlacesTimeout = null;
    }
  }

  private lazyPreloadPlaces(): void {
    this.ngZone.runOutsideAngular(() => {
      this.preloadPlacesTimeout = setTimeout(() => {
        this.ngZone.run(() => {
          this.conditionalReloadPlaces();
        });
      }, 6000);
    });
  }

  private conditionalReloadPlaces(searchState?: SearchState): void {
    const useState: SearchState = searchState ? searchState : this.searchState();
    const payload: SearchResultsPayload = this.searchHelper.generateSearchPayloadFromSearchState(
      useState,
      false,
      this.searchHelper.shouldAskForCampaignOwner(useState),
      this.searchHelper.shouldLimitToOnlyOneItem(useState),
    );

    if (
      this.tools.isNil(this.loadedPlacesPayload) ||
      JSON.stringify(payload) !== JSON.stringify(this.loadedPlacesPayload)
    ) {
      this.loadedPlacesPayload = this.tools.clone(payload);
      payload.includeSearchResult = false;
      this.searchApi.getPlacesResults(payload).subscribe(
        (places) => {
          this.places.set(places);
        },
        (error) => {
          this.places.set(null);
        },
      );
    }
  }
}
